import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ProjectAPI from "@/src/api/ProjectAPI";
import { Button } from "@/src/components/ui/button";
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import useAbility from "@/src/hooks/useAbility";

import DeleteEmbedProfileDialog from "./DeleteEmbedProfileDialog";
import EmbedProfileFormDialog from "./EmbedProfileFormDialog";
import EmbedProfileRow from "./EmbedProfileRow";

import type { JSX } from "react";

import type { EmbedProfile } from "@/src/api/ProjectAPI.types";
import type { AppState } from "@/src/reducers";

function Placeholder({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="text-muted-foreground px-6.25 py-4 text-sm">{children}</p>
  );
}

export default function EmbedProfilesConfiguration(): JSX.Element {
  const { t } = useTranslation("editor-organization");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<EmbedProfile | null>(
    null,
  );
  const [profileToDelete, setProfileToDelete] = useState<EmbedProfile | null>(
    null,
  );

  const { canAccess } = useAbility();
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { embedProfilesWrite } = canAccess(orgaOptions);

  const listQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getEmbedProfileList,
    params: [],
  });
  const { data, isLoading, isError } = useQuery(listQuery);
  const profileList = data?.data ?? [];
  const currentDefault = profileList.find((profile) => profile.default) ?? null;

  const handleCreate = (): void => {
    setEditingProfile(null);
    setIsFormOpen(true);
  };

  const handleEdit = (profile: EmbedProfile): void => {
    setEditingProfile(profile);
    setIsFormOpen(true);
  };

  const handleCloseForm = (): void => setIsFormOpen(false);

  const handleCloseDelete = (): void => setProfileToDelete(null);

  function renderContent(): JSX.Element {
    if (isLoading) {
      return <Placeholder>{t("embedProfiles.loading")}</Placeholder>;
    }
    if (isError) {
      return <Placeholder>{t("embedProfiles.loadError")}</Placeholder>;
    }
    if (profileList.length === 0) {
      return <Placeholder>{t("embedProfiles.empty")}</Placeholder>;
    }
    return (
      <ul>
        {profileList.map((profile) => (
          <EmbedProfileRow
            key={profile.uuid}
            canWrite={embedProfilesWrite}
            onDelete={setProfileToDelete}
            onEdit={handleEdit}
            profile={profile}
          />
        ))}
      </ul>
    );
  }

  return (
    <Card className="mt-3.5 gap-0 py-0">
      <CardHeader className="border-b py-4 [.border-b]:pb-4">
        <CardTitle>{t("embedProfiles.title")}</CardTitle>
        <CardDescription>{t("embedProfiles.subtitle")}</CardDescription>
        {embedProfilesWrite ? (
          <CardAction>
            <Button onClick={handleCreate}>
              <Plus />
              {t("embedProfiles.add")}
            </Button>
          </CardAction>
        ) : null}
      </CardHeader>
      {renderContent()}

      <EmbedProfileFormDialog
        currentDefault={currentDefault}
        onClose={handleCloseForm}
        open={isFormOpen}
        profile={editingProfile}
      />
      <DeleteEmbedProfileDialog
        onClose={handleCloseDelete}
        profile={profileToDelete}
      />
    </Card>
  );
}
